import {
  Button,
  ChoiceRow,
  Field,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
  titleCase,
} from '@westhetics/ui'
import { useId } from 'react'
import { Link } from 'react-router'
import { errorMessage } from '@/lib/errors'
import { routes } from '@/routes'
import { usePractitionerOptions, type MemberRole, type PractitionerOption } from './api'
import { ROLE_DESCRIPTIONS, ROLE_LABELS } from './labels'

/**
 * A escolha do perfil e, quando é prestador, de qual prestadora a pessoa é (RN-ADM-12). Serve à
 * criação do usuário e à troca de papel. A lista de prestadoras só é pedida quando o perfil
 * prestador é escolhido.
 */

const noteClass = 'font-body text-eyebrow mt-[6px] leading-[1.5] font-light tracking-normal'

function practitionerLabel(practitioner: PractitionerOption) {
  return [
    titleCase(practitioner.name),
    practitioner.professionName,
    practitioner.fullRegistration,
    practitioner.user ? 'já tem usuário' : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

export function RoleFields({
  label,
  hideLabel,
  roles,
  role,
  practitionerId,
  onRole,
  onPractitioner,
  onPractitionerBlur,
  errors,
}: {
  /** O rótulo da escolha: "Permissão" na criação, "Permissão nova" na troca. */
  label: string
  /** Quando o título do bloco já diz o que é a escolha, o rótulo fica só para o leitor de tela. */
  hideLabel?: boolean
  /** Os perfis oferecidos; a troca de papel deixa o atual de fora. */
  roles: readonly MemberRole[]
  role: MemberRole | null
  practitionerId: string
  onRole: (role: MemberRole) => void
  onPractitioner: (practitionerId: string) => void
  onPractitionerBlur?: () => void
  errors: { role?: string; practitionerId?: string }
}) {
  const labelId = useId()
  const practitioners = usePractitionerOptions(role === 'practitioner')
  const options = practitioners.data ?? []
  const hasFree = options.some((practitioner) => !practitioner.user)

  return (
    <div className="flex flex-col gap-4">
      <div>
        {hideLabel ? null : (
          <div className="eyebrow mb-[6px]" id={labelId}>
            {label}
          </div>
        )}
        <RadioGroup
          aria-label={hideLabel ? label : undefined}
          aria-labelledby={hideLabel ? undefined : labelId}
          value={role ?? ''}
          onValueChange={(value) => onRole(value as MemberRole)}
        >
          {roles.map((option) => (
            <ChoiceRow
              key={option}
              control={<Radio value={option} />}
              description={ROLE_DESCRIPTIONS[option]}
            >
              {ROLE_LABELS[option]}
            </ChoiceRow>
          ))}
        </RadioGroup>
        {errors.role ? (
          <p role="alert" className={`${noteClass} text-plum`}>
            {errors.role}
          </p>
        ) : null}
      </div>

      {role !== 'practitioner' ? null : practitioners.isPending ? (
        <div role="status" aria-label="Carregando as prestadoras">
          <div className="eyebrow">Prestadora</div>
          <Skeleton className="mt-[6px] h-[42px] w-full" />
        </div>
      ) : practitioners.isError ? (
        <div>
          <div className="eyebrow">Prestadora</div>
          <p role="alert" className={`${noteClass} text-plum`}>
            Não conseguimos carregar as prestadoras. {errorMessage(practitioners.error)}
          </p>
          <Button className="mt-3" variant="text" onClick={() => void practitioners.refetch()}>
            Tentar de novo
          </Button>
        </div>
      ) : options.length === 0 ? (
        <div>
          <div className="eyebrow">Prestadora</div>
          <p className={`${noteClass} text-ink`}>
            Nenhuma prestadora cadastrada nesta clínica. O perfil prestador é de quem já está em
            Prestadores, com conselho e registro.
          </p>
          {errors.practitionerId ? (
            <p role="alert" className={`${noteClass} text-plum`}>
              {errors.practitionerId}
            </p>
          ) : null}
          <Button className="mt-3" variant="text" asChild>
            <Link to={routes.admin.newPractitioner}>Cadastrar a prestadora</Link>
          </Button>
        </div>
      ) : (
        <Field
          label="Prestadora"
          error={errors.practitionerId}
          hint={
            hasFree
              ? 'Liga o usuário ao cadastro de quem responde tecnicamente: define os atendimentos e as anamneses que a pessoa vê.'
              : 'Todas as prestadoras desta clínica já têm usuário. Cada prestadora tem um usuário só.'
          }
        >
          <Select
            placeholder="Selecione"
            value={practitionerId}
            onChange={(event) => onPractitioner(event.target.value)}
            onBlur={onPractitionerBlur}
          >
            {options.map((practitioner) => (
              <option
                key={practitioner.id}
                value={practitioner.id}
                disabled={Boolean(practitioner.user)}
              >
                {practitionerLabel(practitioner)}
              </option>
            ))}
          </Select>
        </Field>
      )}
    </div>
  )
}
